import type { AxiosInstance } from 'axios';
import authService from './auth.service';
import type { QueuePage, SanctionDisposition } from '../types/sanctions';

/** The page size the screen asks for (the server serves at most 100 items a page). */
export const QUEUE_PAGE_SIZE = 25;

class SanctionsService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  /** One page of the open potential-match queue: one item per subject and list entry, its
   *  screenings beneath, and the totals of the whole queue. */
  async listQueue(page: number, pageSize: number = QUEUE_PAGE_SIZE): Promise<QueuePage> {
    const res = await this.api.get<QueuePage>('/api/v1/compliance/sanctions/screenings', {
      params: { page, page_size: pageSize },
    });
    return res.data;
  }

  /** Record a disposition. `false_positive` needs a prior
   *  `proposed_false_positive` by a DIFFERENT reviewer; `confirmed_true_match`
   *  auto-suspends a client — both enforced by the API. */
  async disposition(
    resultId: number,
    disposition: SanctionDisposition,
    reason: string,
  ): Promise<{ id: number; disposition: SanctionDisposition; account_suspended: boolean }> {
    const res = await this.api.post<{
      id: number;
      disposition: SanctionDisposition;
      account_suspended: boolean;
    }>(`/api/v1/compliance/sanctions/screenings/${resultId}/dispositions`, {
      disposition,
      reason,
    });
    return res.data;
  }

  /** How many screenings wait in the whole queue. */
  async openCount(): Promise<number> {
    return (await this.listQueue(1, 1)).total_screenings;
  }
}

export default new SanctionsService();
